import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { parseLocaleNum } from "../lib/locale";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Loader2, Inbox, X, Hammer } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;
const today = () => new Date().toISOString().slice(0, 10);
const emptyItem = { peralatan_id: "", nama_item_snapshot: "", qty: 0, harga_snapshot: 0, save_to_master: false };
const emptyForm = { vendor_id: "", spk_id: "none", tanggal: today(), items: [{ ...emptyItem }], catatan: "" };

export default function BonPeralatanPage() {
    const { canWrite, canDelete } = useAuth();
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [spkList, setSpkList] = useState([]);
    const [peralatanList, setPeralatanList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/bon-peralatan", { params: q ? { q } : {} });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        (async () => {
            const [v, s, p] = await Promise.all([api.get("/vendors"), api.get("/spk", { params: { status: "aktif" } }), api.get("/master/peralatan")]);
            setVendors(v.data); setSpkList(s.data); setPeralatanList(p.data);
        })();
    }, []);
    useEffect(() => { const t = setTimeout(() => { load(); setPage(1); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.nama])), [vendors]);
    const spkForVendor = useMemo(() => spkList.filter((s) => !form.vendor_id || s.vendor_id === form.vendor_id), [spkList, form.vendor_id]);
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);

    const openCreate = () => { setEditing(null); setForm({ ...emptyForm, items: [{ ...emptyItem }] }); setDialogOpen(true); };
    const openEdit = (row) => {
        setEditing(row);
        setForm({
            vendor_id: row.vendor_id, spk_id: row.spk_id || "none", tanggal: row.tanggal,
            items: row.items.map((i) => ({ peralatan_id: i.peralatan_id || "", nama_item_snapshot: i.nama_item_snapshot, qty: i.qty, harga_snapshot: i.harga_snapshot, save_to_master: false })),
            catatan: row.catatan || "",
        });
        setDialogOpen(true);
    };

    const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
    const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
    const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
    const pickPeralatan = (idx, id) => {
        if (id === "__manual__") { setItem(idx, { peralatan_id: "", nama_item_snapshot: "" }); return; }
        const p = peralatanList.find((x) => x.id === id);
        setItem(idx, { peralatan_id: id, nama_item_snapshot: p?.nama_item || "", harga_snapshot: p?.harga || 0 });
    };

    const totals = form.items.map((i) => parseLocaleNum(i.qty || 0) * parseLocaleNum(i.harga_snapshot || 0));
    const totalNilai = totals.reduce((s, x) => s + x, 0);

    const save = async () => {
        if (!form.vendor_id) return toast.error("Vendor wajib dipilih");
        if (form.items.length === 0) return toast.error("Minimal 1 item peralatan");
        for (const it of form.items) {
            if (!it.nama_item_snapshot?.trim()) return toast.error("Nama item wajib diisi");
            if (parseLocaleNum(it.qty) <= 0) return toast.error("Qty harus > 0");
            if (parseLocaleNum(it.harga_snapshot) < 0) return toast.error("Harga tidak boleh negatif");
        }
        setSubmitting(true);
        try {
            // Save new items to master if checked
            for (const it of form.items) {
                if (it.save_to_master && !it.peralatan_id && it.nama_item_snapshot?.trim()) {
                    try {
                        const { data } = await api.post("/master/peralatan", {
                            nama_item: it.nama_item_snapshot, harga: parseFloat(it.harga_snapshot),
                            satuan: "pcs", keterangan: "Auto dari Bon Peralatan",
                        });
                        it.peralatan_id = data.id;
                    } catch { /* silent */ }
                }
            }
            const payload = {
                vendor_id: form.vendor_id,
                spk_id: form.spk_id === "none" ? "" : form.spk_id,
                tanggal: form.tanggal,
                items: form.items.map((i) => ({
                    peralatan_id: i.peralatan_id || "",
                    nama_item_snapshot: i.nama_item_snapshot.trim(),
                    qty: parseFloat(i.qty),
                    harga_snapshot: parseFloat(i.harga_snapshot),
                })),
                catatan: form.catatan,
            };
            if (editing) { await api.put(`/bon-peralatan/${editing.id}`, payload); toast.success("Bon Peralatan diperbarui"); }
            else { await api.post("/bon-peralatan", payload); toast.success("Bon Peralatan ditambahkan"); }
            setDialogOpen(false); load();
            // Refresh peralatan master
            const p = await api.get("/master/peralatan"); setPeralatanList(p.data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try { await api.delete(`/bon-peralatan/${confirmDelete.id}`); toast.success("Dipindahkan ke Riwayat Terhapus. Buka Riwayat untuk pulihkan."); setConfirmDelete(null); load(); }
        catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    return (
        <div data-testid="bon-peralatan-page">
            <PageHeader
                title="Bon Peralatan"
                description="Bahan pembantu keluar untuk vendor (bisa terkait SPK)"
                actions={canWrite() && <Button onClick={openCreate} data-testid="bon-peralatan-add-button" className="rounded-lg gap-2 text-white font-medium" style={{ background: "var(--apple-blue)" }}><Plus className="w-4 h-4" /> Tambah Bon</Button>}
            />

            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="relative max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                    <Input placeholder="Cari nomor bon..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="bon-peralatan-search-input" className="pl-9 rounded-lg h-10" />
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={Hammer} title="Belum ada bon peralatan" description="Belum ada bon peralatan tercatat." testid="bon-peralatan-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => {
                    const spkNo = row.spk_id ? (spkList.find((s) => s.id === row.spk_id)?.nomor_spk || "-") : "-";
                    return (
                        <div key={row.id} className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--border)" }} data-testid={`bon-peralatan-card-${row.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm" style={{ color: "var(--apple-blue)" }}>{row.nomor_bon}</div>
                                    <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || "-"}</div>
                                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>SPK: {spkNo}</div>
                                </div>
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal)}</div>
                            </div>
                            <div className="flex items-center justify-between pt-2 mt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                                <div className="text-sm font-semibold num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.total_nilai || 0)}</div>
                                <div className="flex items-center gap-1">
                                    {canWrite() && <button onClick={() => openEdit(row)} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }}><Pencil className="w-4 h-4" /></button>}
                                    {canDelete() && <button onClick={() => setConfirmDelete(row)} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="bon-peralatan-data-table">
                    <TableHeader>
                        <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Nomor Bon</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">SPK</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Tanggal</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500 text-right">Total Nilai</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase text-slate-500 w-28">Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} className="py-12 text-center"><Loader2 className="w-5 h-5 text-teal-600 animate-spin mx-auto" /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="py-14 text-center"><div className="flex flex-col items-center gap-2 text-slate-400"><Inbox className="w-8 h-8" /><div className="text-sm">Belum ada bon peralatan</div></div></TableCell></TableRow>
                        ) : pageItems.map((row) => {
                            const spkNo = row.spk_id ? (spkList.find((s) => s.id === row.spk_id)?.nomor_spk || "-") : "-";
                            return (
                                <TableRow key={row.id} data-testid={`bon-peralatan-row-${row.id}`} className="border-b border-slate-100/80 hover:bg-teal-50/40 transition-colors">
                                    <TableCell className="font-semibold text-teal-700">{row.nomor_bon}</TableCell>
                                    <TableCell className="text-sm text-slate-800">{vendorMap[row.vendor_id] || "-"}</TableCell>
                                    <TableCell className="text-sm text-slate-600">{spkNo}</TableCell>
                                    <TableCell className="text-sm text-slate-600">{formatTanggal(row.tanggal)}</TableCell>
                                    <TableCell className="text-sm font-semibold text-slate-900 text-right num-tabular">{formatRupiah(row.total_nilai || 0)}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {canWrite() && <button onClick={() => openEdit(row)} data-testid={`bon-peralatan-edit-btn-${row.id}`} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-teal-700"><Pencil className="w-4 h-4" /></button>}
                                            {canDelete() && <button onClick={() => setConfirmDelete(row)} data-testid={`bon-peralatan-delete-btn-${row.id}`} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/40">
                        <div className="text-xs text-slate-500">Menampilkan {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length}</div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs text-slate-600 px-2">Hal. {page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="bon-peralatan-modal-dialog" className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Bon Peralatan" : "Tambah Bon Peralatan"}</DialogTitle>
                        <DialogDescription>Pilih peralatan dari master, atau input item baru (opsional simpan ke master).</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <Label>Vendor <span className="text-rose-600">*</span></Label>
                                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v, spk_id: "none" })} disabled={!!editing}>
                                    <SelectTrigger data-testid="bon-peralatan-form-vendor" className="mt-1.5 rounded-xl"><SelectValue placeholder="Pilih vendor" /></SelectTrigger>
                                    <SelectContent>
                                        {vendors.filter((v) => v.status === "aktif").map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>SPK (opsional)</Label>
                                <Select value={form.spk_id} onValueChange={(v) => setForm({ ...form, spk_id: v })}>
                                    <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue placeholder="Tanpa SPK" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— Tanpa SPK —</SelectItem>
                                        {spkForVendor.map((s) => <SelectItem key={s.id} value={s.id}>{s.nomor_spk}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Tanggal <span className="text-rose-600">*</span></Label>
                                <Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} className="mt-1.5 rounded-xl" />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label>Item <span className="text-rose-600">*</span></Label>
                                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="bon-peralatan-form-add-item" className="rounded-lg h-8 gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Item</Button>
                            </div>
                            <div className="space-y-2">
                                {form.items.map((it, idx) => (
                                    <div key={idx} data-testid={`bon-peralatan-form-item-${idx}`} className="p-3 rounded-xl bg-slate-50/70 border border-slate-200/60 space-y-2">
                                        <div className="grid grid-cols-12 gap-2">
                                            <div className="col-span-12 sm:col-span-5">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Peralatan</div>
                                                <Select value={it.peralatan_id || "__manual__"} onValueChange={(v) => pickPeralatan(idx, v)}>
                                                    <SelectTrigger className="rounded-lg h-9 bg-white"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="__manual__">✏️ Input manual (item baru)</SelectItem>
                                                        {peralatanList.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama_item}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                                {!it.peralatan_id && (
                                                    <Input value={it.nama_item_snapshot} onChange={(e) => setItem(idx, { nama_item_snapshot: e.target.value })} placeholder="Nama item baru" className="rounded-lg h-9 bg-white mt-1.5" />
                                                )}
                                            </div>
                                            <div className="col-span-4 sm:col-span-2">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Qty</div>
                                                <Input type="text" inputMode="decimal" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} className="rounded-lg h-9 bg-white" />
                                            </div>
                                            <div className="col-span-4 sm:col-span-3">
                                                <div className="text-[10px] uppercase text-slate-500 mb-1">Harga Satuan</div>
                                                <Input type="text" inputMode="decimal" value={it.harga_snapshot} onChange={(e) => setItem(idx, { harga_snapshot: e.target.value })} className="rounded-lg h-9 bg-white" />
                                            </div>
                                            <div className="col-span-4 sm:col-span-2 flex sm:items-end sm:justify-end">
                                                {form.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="text-rose-600 hover:bg-rose-50 p-2 rounded-lg h-9"><X className="w-4 h-4" /></button>}
                                            </div>
                                        </div>
                                        {!it.peralatan_id && it.nama_item_snapshot?.trim() && (
                                            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                                                <Checkbox checked={!!it.save_to_master} onCheckedChange={(v) => setItem(idx, { save_to_master: !!v })} />
                                                Simpan item baru ini ke Master Peralatan
                                            </label>
                                        )}
                                        <div className="text-right text-xs text-slate-500 num-tabular">Subtotal: <span className="font-semibold text-slate-800">{formatRupiah(totals[idx])}</span></div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 text-right text-sm text-slate-600">Total Nilai: <span className="font-bold text-teal-700 num-tabular">{formatRupiah(totalNilai)}</span></div>
                        </div>

                        <div>
                            <Label>Catatan</Label>
                            <Textarea value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} className="mt-1.5 rounded-xl" rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Batal</Button>
                        <Button onClick={save} disabled={submitting} data-testid="bon-peralatan-modal-save-button" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl">
                    <AlertDialogHeader><AlertDialogTitle>Hapus Bon Peralatan (ke Riwayat)</AlertDialogTitle><AlertDialogDescription><span className="font-semibold">{confirmDelete?.nomor_bon}</span> akan dipindahkan ke <b>Riwayat Terhapus</b> selama 30 hari dan bisa dipulihkan kapan saja. Lanjut?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-button" className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">{deleting ? "..." : "Hapus (ke Riwayat)"}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
